import React from 'react';
import { cn } from 'v5/platform/dom/cn';

// Table Root Component
export interface TableRootProps extends React.HTMLAttributes<HTMLTableElement> {
  children: React.ReactNode;
}

export const TableRoot = React.forwardRef<HTMLTableElement, TableRootProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <table
        ref={ref}
        className={cn('w-full border-collapse bg-bg-default', className)}
        {...props}
      >
        {children}
      </table>
    );
  }
);

TableRoot.displayName = 'TableRoot';

// Table Header Component
interface TableHeaderProps
  extends React.HTMLAttributes<HTMLTableSectionElement> {
  children: React.ReactNode;
}

export const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  TableHeaderProps
>(({ className, children, ...props }, ref) => {
  return (
    <thead ref={ref} className={cn('', className)} {...props}>
      {children}
    </thead>
  );
});

TableHeader.displayName = 'TableHeader';

// Table Body Component
interface TableBodyProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  children: React.ReactNode;
}

export const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  TableBodyProps
>(({ className, children, ...props }, ref) => {
  return (
    <tbody ref={ref} className={cn('', className)} {...props}>
      {children}
    </tbody>
  );
});

TableBody.displayName = 'TableBody';

// Table Row Component
interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  children: React.ReactNode;
}

export const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <tr
        ref={ref}
        className={cn(
          'transition-colors border-b border-border-default  aria-selected:bg-fg-default/3 aria-selected:hocus:bg-fg-default/4',
          props.onClick && 'hocus:bg-fg-default/3',
          className
        )}
        {...props}
      >
        {children}
      </tr>
    );
  }
);

TableRow.displayName = 'TableRow';

// Table Header Cell Component
interface TableHeaderCellProps
  extends React.ThHTMLAttributes<HTMLTableCellElement> {
  children: React.ReactNode;
}

export const TableHeaderCell = React.forwardRef<
  HTMLTableCellElement,
  TableHeaderCellProps
>(({ className, children, ...props }, ref) => {
  return (
    <th
      ref={ref}
      className={cn(
        'px-4 py-3 text-left text-sm uppercase font-medium text-fg-muted bg-bg-default not-last:border-r border-border-default border-b',
        className
      )}
      {...props}
    >
      {children}
    </th>
  );
});

TableHeaderCell.displayName = 'TableHeaderCell';

// Table Cell Component
interface TableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  children: React.ReactNode;
}

export const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <td
        ref={ref}
        className={cn(
          'px-4 py-3 text-base text-fg-default',
          'not-last:border-r not-last:border-border-default',
          '[&_a]:focus-visible:underline',
          className
        )}
        {...props}
      >
        {children}
      </td>
    );
  }
);

TableCell.displayName = 'TableCell';

// Table Container Component (for styling wrapper)
interface TableContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export const TableContainer = React.forwardRef<
  HTMLDivElement,
  TableContainerProps
>(({ className, children, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        // Full border sits on the scrollport box — always visible while scrolling.
        // Last row of the last tbody drops border-b so it does not stack on the
        // container/spacer edge. Only `:last-of-type` — with one tbody per rowspan
        // group, `tbody>tr:last-child` would drop borders between groups.
        // Spacer border-t only paints when there is free height (short table),
        // drawing the line under the last row above empty fill.
        'flex flex-col overflow-x-auto border border-border-default bg-bg-default [&_tbody:last-of-type>tr:last-child]:border-b-0',
        className
      )}
      {...props}
    >
      {children}
      <div
        aria-hidden
        className="pointer-events-none min-h-0 flex-1 basis-0 overflow-hidden"
      >
        <div className="h-full border-t border-border-default" />
      </div>
    </div>
  );
});

TableContainer.displayName = 'TableContainer';

// Export all components as a compound component
export const Table = {
  Root: TableRoot,
  Header: TableHeader,
  Body: TableBody,
  Row: TableRow,
  HeaderCell: TableHeaderCell,
  Cell: TableCell,
  Container: TableContainer
};
